<script setup>
import { useDrawingStore } from '@/store/drawing'
import { can } from '@layouts/plugins/casl'
import {
  $getSign,
  $signOptions,
  $getSignActivityTimeline,
  $updateSign,
} from '@/api/signs'
import { useUserStore } from '@/store/user'
import FileManagementPhotosBlock from '@/components/projects/signs/sign-modal/FileManagementPhotosBlock.vue'
import { $getCampaign } from '@/api/campaigns'
import { useSnackbarStore } from '@/store/snackbar'
import SignDialogNotesBlock from '@/components/projects/signs/sign-modal/SignDialogNotesBlock.vue'
import ability from "@/plugins/casl/ability";
import { onWaitingRoomAutoSyncRefresh } from '@/helpers/events/waitingRoomAutoSync'
import { useWaitingRoomAutoSyncStore } from '@/store/waitingRoomAutoSync'

const router = useRouter()
const route = useRoute()

const signActivityTimelineSidebar = ref()

const dialogVisible = ref(false)
const drawingStore = useDrawingStore()
const userStore = useUserStore()
const snackbarStore = useSnackbarStore()
const waitingRoomAutoSyncStore = useWaitingRoomAutoSyncStore()

const sign = ref({})
const signPriorityOptions = ref([])

const props = defineProps({
  signId: {
    type: Number,
    default: null
  },
  isSignReprintBlock: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['sign-updated'])

const project = ref({})
const signLoading = ref(false)

const updateBtnLoading = ref(false)
const printBtnLoading = ref(false)

const isArtworkPdfRendered = ref(false)
const isPrepressProofPdfRendered = ref(false)

const signTypes = ref([])
const signTemplateTypes = ref([])
const funcAreas = ref([])

const photoTab = ref(0)

const signGlossaryRowsBySide = ref({ 0: [], 1: [], 2: [], 3: []})

const showSignStatusHistoryDialog = ref(false)
const showCopySignContentToSignsDialog = ref(false)

const sides = ['A', 'B', 'C', 'D']
const signSides = ref(1)

const showSidebar = ref(false)
const signActivityTimelinePerPage = ref(20)
const signActivityTimelinePage = ref(1)
const signActivityTimelineIsNextPageExist = ref(false)
const signActivityTimelineItems = ref([])
const signActivityTimelineLoading = ref(false)

const showGlobalChatDialog = ref(false)

const showSignArtworkReviewDialog = ref(false)
const showSignPrepressProofReviewDialog = ref(false)

const showSignActivityQuery = computed(() => {
  return route.query['show-sign-activity']
})


const isPrintBtnDisabled = computed(() => {
  if (!!printBtnLoading.value) {
    return true
  }
  else if (!sign.value?.id) {
    return true
  }
  return false
})

const isDisabledSignFields = computed(() => {
  return !(isUserCanChangeSign.value && sign.value?.is_active_campaign)
})

const isUserCanChangeSign = computed(() => {
  return ability.can('change', 'sign')
})

const getSignOptions = async () => {
  try {
    const res = await $signOptions()
    signPriorityOptions.value = res.data.actions.POST.priority.choices
  } catch (e) {
    console.log(e)
  }
}

const onSelectPriority = async (priorityValue) => {
  if (priorityValue !== sign.value.priority) {
    signLoading.value = true
    try {
      const res = await $updateSign(sign.value.id, { priority: priorityValue })
      handleSignData(res.data)
      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Success',
        text: 'Sign Priority updated successfully!'
      })
    } catch (e) {
      console.log(e)
      const mes = e.response.data ? e.response.data : e.message
      snackbarStore.showMessage({
        color: 'error',
        icon: 'mdi-alert-circle',
        title: 'Error',
        text: `Oops, something went wrong. ${mes}`
      })
    } finally {
      signLoading.value = false
    }
  }
}

const updateSignIsOnHold = async () => {
  signLoading.value = true
  try {
    const res = await $updateSign(sign.value.id, { is_on_hold: sign.value.is_on_hold })
    handleSignData(res.data)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'On Hold option updated successfully!'
    })
  } catch (e) {
    console.log(e)
    const mes = e.response.data ? e.response.data : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    signLoading.value = false
  }
}

const handleSignBlockData = (data) => {
  signSides.value = data.type_info.sides

  sign.value.sign_glossary_rows = data.sign_glossary_rows

  signGlossaryRowsBySide.value = { 0: [], 1: [], 2: [], 3: [] }
  sign.value.sign_glossary_rows.forEach((row) => {
    const sideNumber = row.side_number

    // Push the row to the corresponding array
    signGlossaryRowsBySide.value[sideNumber].push(row)
  })
}

const handleSignData = (data) => {
  sign.value = data

  sign.value.number = data.short_name

  if (!sign.value.width) {
    sign.value.width = data.type_info.width
  }

  if (!sign.value.height) {
    sign.value.height = data.type_info.height
  }

  sign.value.is_custom = data.type_info.is_custom

  handleSignBlockData(data)
  getSignActivityTimelineItems(true)
}

const getProject = async () => {
  const res = await $getCampaign(sign.value.campaign)
  project.value = res.data
  signTypes.value = res.data.sign_type_list
  funcAreas.value = res.data.functional_area_list
}

const getSign = async (updateSignBlocksOnly = false) => {
  signLoading.value = true
  try {
    if(props.signId) {
      const res = await $getSign(props.signId)
      if (updateSignBlocksOnly) {
        handleSignBlockData(res.data)
      } else {
        handleSignData(res.data)
      }
      await getProject()
    }
  } catch (e) {
    console.log(e)
  } finally {
    signLoading.value = false
  }
}

const unsubscribeWaitingRoomAutoSyncRefresh = onWaitingRoomAutoSyncRefresh(result => {
  const isCurrentSignAffected = result.affected_targets.some(target => (
    target.target_type === 'sign' && String(target.target_id) === String(props.signId)
  ))

  if (dialogVisible.value && isCurrentSignAffected) {
    void getSign()
  }
})

const updateSign = async () => {
  updateBtnLoading.value = true
  try {
    delete sign.value.map_snapshot_file
    const res = waitingRoomAutoSyncStore.handleResponse(await $updateSign(sign.value.id, sign.value))
    handleSignData(res.data)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: `Sign ${sign.value.is_reprint ? 'Reprint ' : ''}updated successfully!`
    })
  } catch (e) {
    console.log(e)
  } finally {
    updateBtnLoading.value = false
  }
}

const onSignTypeChange = () => {
  const newSignType = signTypes.value.find(i => i.id === sign.value.type)

  sign.value.width = newSignType?.width || ''
  sign.value.height = newSignType?.height || ''

  if (newSignType && !newSignType.is_custom) {
    sign.value.is_custom = false
  }
  else if (newSignType && newSignType.is_custom) {
    sign.value.is_custom = true
  }
}

const onCloseDialog = () => {
  emit('sign-updated')
}

const getSignActivityTimelineItems = async (isClear = false) => {
  // Fetch Activity Timeline Items only when the sidebar is visible
  if (!showSidebar.value) return

  const modalSidebarEl = document.querySelector('.modal-sidebar')

  if (!modalSidebarEl) return

  // Save the current scroll height before fetching new items
  const previousScrollHeight = modalSidebarEl.scrollHeight

  if (isClear) {
    signActivityTimelinePage.value = 1
    signActivityTimelineItems.value = []
  }

  signActivityTimelineLoading.value = true
  try {
    const res = await $getSignActivityTimeline(props.signId, {
      per_page: signActivityTimelinePerPage.value,
      page: signActivityTimelinePage.value
    })
    signActivityTimelineItems.value = [...res.data.results, ...signActivityTimelineItems.value]
    signActivityTimelineIsNextPageExist.value = !!res.data.next

    if (isClear) {
      // Scroll sidebar to the bottom after next DOM update
      await nextTick(() => {
        signActivityTimelineSidebar.value?.scrollSidebarToBottom()
      })
    } else {
      // Wait for the DOM to update with the new items
      await nextTick()

      // Calculate the new scroll height and adjust the scroll position
      const newScrollHeight = modalSidebarEl.scrollHeight
      const heightDifference = newScrollHeight - previousScrollHeight
      modalSidebarEl.scrollTop = heightDifference
    }
  } catch (e) {
    console.log(e)
  } finally {
    signActivityTimelineLoading.value = false
  }
}

const closeDialog = () => {
  dialogVisible.value = false
}

const handleKeydown = (e) => {
  // Close the dialog if the ESC key is pressed
  if (e.key === 'Escape' && !!dialogVisible.value) {
    // If the sign artwork review dialog is open, close it
    if (showSignArtworkReviewDialog.value) {
      showSignArtworkReviewDialog.value = false
    }
    // If the sign prepress proof review dialog is open, close it
    else if (showSignPrepressProofReviewDialog.value) {
      showSignPrepressProofReviewDialog.value = false
    }
    // If no other dialogs are open, close the main dialog
    else {
      closeDialog()
    }
  }
}

const deleteCurrentSignReprint = async () => {
  if (!sign.value?.id) return

  signLoading.value = true
  try {
    await $updateSign(sign.value.id, { is_removed: true })
    closeDialog()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Sign Reprint deleted successfully!'
    })
  } catch (e) {
    console.log(e)
  } finally {
    signLoading.value = false
  }
}

const updateSignArtworkInfo = (signArtworkInfo) => {
  sign.value.artwork_file = signArtworkInfo?.artwork_file || null
  sign.value.artwork_file_updated_at = signArtworkInfo?.artwork_file_updated_at || null
  sign.value.artwork_file_links = signArtworkInfo?.artwork_file_links || []
  sign.value.prepress_proof_file = signArtworkInfo?.prepress_proof_file || null
  sign.value.prepress_proof_file_updated_at = signArtworkInfo?.prepress_proof_file_updated_at || null
  sign.value.prepress_proof_file_links = signArtworkInfo?.prepress_proof_file_links || []
  sign.value.responsible = signArtworkInfo?.responsible || null
  sign.value.responsible_list = signArtworkInfo?.responsible_list || []
  sign.value.responsible_name = signArtworkInfo?.responsible_name || ''
  sign.value.sign_status = signArtworkInfo?.sign_status || null
  sign.value.sign_status_name = signArtworkInfo?.sign_status_name || null
  sign.value.is_can_user_approve = signArtworkInfo?.is_can_user_approve || false
  sign.value.is_can_user_select = signArtworkInfo?.is_can_user_select || false
  sign.value.is_rejected_back_status = signArtworkInfo?.is_rejected_back_status || false
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  if (route.query.sign && Number(route.query.sign) === props.signId) {
    dialogVisible.value = true
    getSign()
    updateQueryParameters((newQuery) => {
      delete newQuery['sign']
    })
  }
})

// Cleanup event listener when component unmounts
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  unsubscribeWaitingRoomAutoSyncRefresh()
})

watch(() => props.signId, () => {
  getSign()
  getSignOptions()
})

// // Reusable function to silently update the query without causing a page reload, onMounted and navigation guard methods
// const updateQueryParameters = (updateFunction) => {
//   // Create a copy of the route query parameters
//   const newQuery = { ...route.query }
//
//   // Call the provided update function to modify the query
//   updateFunction(newQuery)
//
//   // Get the current path without the query string
//   const path = window.location.pathname
//
//   // Build the new URL with the updated query parameters
//   const newUrl = `${path}?${new URLSearchParams(newQuery).toString()}`
//
//   // Replace the current state with the updated URL
//   window.history.replaceState(null, '', newUrl)
// }

// Reusable function to update query parameters
const updateQueryParameters = (updater) => {
  const newQuery = { ...route.query }
  updater(newQuery)
  router.replace({ query: newQuery })
}

// Function to delete the 'show-sign-activity' query parameter
const deleteShowSignActivityQuery = () => {
  updateQueryParameters((newQuery) => {
    delete newQuery['sign']
    delete newQuery['show-sign-activity']
  })
}

watch(dialogVisible, () => {
  // on show Dialog
  if (!!dialogVisible.value) {
    if (showSignActivityQuery.value === 'true') {
      showSidebar.value = true
      deleteShowSignActivityQuery()
    }
  }
  // on hide Dialog
  else {
  }
})

watch(showSidebar, () => {
  // on show Sidebar
  if (!!showSidebar.value) {
    getSignActivityTimelineItems(true)
  }
  // on hide Sidebar
  else {
    isArtworkPdfRendered.value = false
    isPrepressProofPdfRendered.value = false
  }
})

// Provide the method to the child components
provide('executeGetSign', getSign)
</script>

<template>
  <div>
    <ItemStatusHistoryDialog
      v-model="showSignStatusHistoryDialog"
      :item-id="sign?.id"
      item-type="wayfinding"
      :code="sign?.short_name || ''"
    />
    <CopySignContentToSignsDialog
      v-model="showCopySignContentToSignsDialog"
      :sign="sign"
    />
    <GlobalChatDialog
      v-model="showGlobalChatDialog"
      :item-id="signId"
      :custom-message-input-id="'sign-dialog-chat-comment-custom-input'"
      :can-add-comment="true"
      item-type="wayfinding"
    />
    <VDialog
      v-model="dialogVisible"
      class="sign-modal"
      width="920"
      persistent
      @update:model-value="getSign(false); getSignOptions()"
      @after-leave="onCloseDialog"
    >
      <template #activator="{ props }">
        <div v-if="isSignReprintBlock">
          <VBtn
            v-bind="props"
            size=""
            variant="outlined"
            class="ml-1 padding-all-1"
          >
            <VIcon
              icon="tabler-zoom-scan"
              size="14"
            />
            <VMenu
              activator="parent"
              class="default-tooltip"
              close-delay="0"
              location="top center"
              offset="5"
              open-delay="100"
              open-on-hover
            >
              <span>View</span>
            </VMenu>
          </VBtn>
        </div>
        <div v-else>
          <VBtn
            v-bind="props"
            size="small"
          >
            View
          </VBtn>
        </div>
      </template>

      <DialogCloseBtn
        @click="closeDialog"
      />

      <VCard
        :loading="!!signLoading"
      >
        <template #title>
          <SignDialogTitle
            v-model:show-sidebar="showSidebar"
            v-model:show-global-chat-dialog="showGlobalChatDialog"
            v-model:show-sign-status-history-dialog="showSignStatusHistoryDialog"
            :sign="sign"
            @getSign="getSign"
            @updateSignArtworkInfo="updateSignArtworkInfo"
          />
        </template>
        <SignDialogHeader
          v-model:sign="sign"
          :sign-loading="signLoading"
          :sign-priority-options="signPriorityOptions"
          @onSelectPriority="onSelectPriority"
          @onUpdateSignIsOnHold="updateSignIsOnHold"
        />
        <SignDialogPriceBlock
          v-if="can('view', 'finances')"
          :sign="sign"
        />
        <SignDialogResponsibleBlock
          :sign="sign"
        />
        <VRow>
          <VCol
            cols="12"
            md="7"
          >
            <VCardText>
              <VRow>
                <VCol
                  cols="12"
                  sm="6"
                >
                  <VLabel>Sign Code:</VLabel><br />
                  <b>{{ sign.short_name }}</b>
                </VCol>
                <VCol
                  cols="12"
                  sm="6"
                >
                  <VSelect
                    v-model="sign.type"
                    :items="signTypes"
                    :disabled="!!isDisabledSignFields"
                    item-title="name"
                    item-value="id"
                    label="Sign Type"
                    @update:model-value="onSignTypeChange"
                  />
                </VCol>
                <SignDialogTemplateTypeBlock
                  v-model:sign="sign"
                />
                <VCol
                  cols="12"
                  sm="8"
                >
                  <VAutocomplete
                    v-model="sign.additional_functional_areas"
                    :items="funcAreas"
                    :disabled="!!isDisabledSignFields"
                    item-title="name"
                    item-value="id"
                    density="compact"
                    label="Functional Areas"
                    multiple
                    chips
                    closable-chips
                    no-data-text="Functional Areas not found"
                  >
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        :text="item.raw.name"
                      />
                    </template>
                    <template v-slot:item="{ props, item }">
                      <v-list-item
                        v-bind="props"
                        :title="item.raw.name"
                      />
                    </template>
                  </VAutocomplete>
                </VCol>
                <VCol
                  cols="12"
                  sm="4"
                >
                  <VSelect
                    v-model="sign.orientation"
                    :items="[{value: 0, title: 'Portrait'}, { value: 1, title: 'Landscape'}]"
                    :disabled="!!isDisabledSignFields || sign.is_reprint"
                    label="Orientation"
                  />
                </VCol>
                <VCol
                  cols="12"
                  sm="4"
                >
                  <VTextField
                    type="number"
                    v-model="sign.width"
                    :disabled="isDisabledSignFields || (!sign.is_custom && !sign.is_reprint)"
                    label="Width"
                  />
                </VCol>
                <VCol
                  cols="12"
                  sm="4"
                >
                  <VTextField
                    type="number"
                    v-model="sign.height"
                    :disabled="isDisabledSignFields || (!sign.is_custom && !sign.is_reprint)"
                    label="Height"
                  />
                </VCol>
                <VCol
                  cols="12"
                  sm="4"
                >
                  <VTextField
                    type="number"
                    v-model="sign.qty"
                    :disabled="isDisabledSignFields"
                    label="Qty"
                  />
                </VCol>
              </VRow>
            </VCardText>
            <SignDialogNotesBlock
              v-model:sign="sign"
            />
          </VCol>
          <VCol
            cols="12"
            md="5"
          >
            <VTabs v-model="photoTab">
              <VTab v-if="sign?.prepress_proof_file || sign?.prepress_proof_file_links?.length">Prepress Proof</VTab>
              <VTab>Artwork</VTab>
              <VTab>Photos</VTab>
              <VTab>{{ sign.is_reprint ? 'Reprint Info' : 'Reprint' }}</VTab>
            </VTabs>
            <VWindow v-model="photoTab">
              <VWindowItem
                v-if="sign?.prepress_proof_file || sign?.prepress_proof_file_links?.length"
                class="pa-1 h-100"
              >
                <SignDialogArtworkBlock
                  v-model:show-sign-artwork-review-dialog="showSignPrepressProofReviewDialog"
                  :sign="sign"
                  :signSides="signSides"
                  :sides="sides"
                  :file-type="'prepress_proof'"
                  @onArtworkPdfRendered="isPrepressProofPdfRendered = true"
                />
              </VWindowItem>
              <VWindowItem class="pa-1 h-100">
                <SignDialogArtworkBlock
                  v-model:show-sign-artwork-review-dialog="showSignArtworkReviewDialog"
                  :sign="sign"
                  :signSides="signSides"
                  :sides="sides"
                  :file-type="'artwork'"
                  @onArtworkPdfRendered="isArtworkPdfRendered = true"
                />
              </VWindowItem>
              <VWindowItem>
                <div
                  class="mt-2 pl-2 pl-md-0"
                  style="height: 630px; overflow: visible;"
                >
                  <FileManagementPhotosBlock
                    :can-add="!isDisabledSignFields"
                    :can-delete="!isDisabledSignFields"
                    :item-id="props.signId"
                    :item-code="sign?.main_full_code || sign?.short_name || null"
                    :project-id="sign?.campaign || null"
                    :project-type="1"
                  />
                </div>
              </VWindowItem>
              <VWindowItem>
                <div
                  class="mt-2 pl-2 pl-md-0"
                  style="height: 630px; overflow: scroll;"
                >
                  <template v-if="sign.is_reprint">
                    <SignReprintFieldsBlock
                      v-model:sign="sign"
                    />
                  </template>
                  <template v-else>
                    <SignReprintListBlock
                      v-if="sign?.is_active_campaign"
                      :sign="sign"
                    />
                    <template v-else>
                      <VAlert
                        show
                        variant="tonal"
                        color="warning"
                        class="text-body-1 py-1 max-width-200 position-relative text-center cursor-default mx-auto"
                      >
                        Inactive Project Sign
                        <VIcon
                          size="small"
                          icon="tabler-info-circle"
                        />
                        <VMenu
                          activator="parent"
                          location="top center"
                          open-on-hover
                          open-delay="100"
                          close-delay="0"
                          offset="5"
                          class="default-tooltip"
                        >
                          <span class="px-2 text-center">
                            No changes allowed
                          </span>
                        </VMenu>
                      </VAlert>
                    </template>
                  </template>
                </div>
              </VWindowItem>
            </VWindow>
          </VCol>
        </VRow>
        <VContainer fluid>
          <VRow>
            <VCol>
              <SignDialogGlossaryBlock
                :sign="sign"
                :signSides="signSides"
                :sign-glossary-rows-by-side="signGlossaryRowsBySide"
                :sides="sides"
                @handleSignBlockData="handleSignBlockData($event)"
                @getSignBlocks="getSign(true)"
                @getActivityTimelineItems="getSignActivityTimelineItems(true)"
              />
            </VCol>
          </VRow>
        </VContainer>
        <VCardText>
          <div class="d-flex justify-space-between align-center flex-wrap">
            <div>
              <VBtn
                v-if="can('change', 'sign') && sign.is_active_campaign"
                :disabled="!!updateBtnLoading"
                class="ma-1"
                @click="updateSign"
              >
                Update
                <VProgressCircular
                  v-if="!!updateBtnLoading"
                  class="ml-1"
                  color="light"
                  indeterminate
                  size="16"
                  width="2"
                />
              </VBtn>
              <VBtn
                v-if="can('delete', 'sign') && sign?.is_reprint && sign.is_active_campaign"
                class="ma-1"
                variant="tonal"
                @click="deleteCurrentSignReprint"
              >
                Delete Reprint
              </VBtn>
            </div>
            <div class="d-flex flex-wrap">
              <VBtn
                v-if="can('change', 'sign') && !sign.is_reprint && sign.is_active_campaign"
                :disabled="!sign?.id"
                class="ma-1"
                color="default"
                variant="tonal"
                @click="showCopySignContentToSignsDialog = true"
              >
                Copy Content
                <VMenu
                  activator="parent"
                  class="default-tooltip"
                  close-delay="0"
                  location="top center"
                  offset="5"
                  open-delay="100"
                  open-on-hover
                >
                  <span>Copy Content to Signs</span>
                </VMenu>
              </VBtn>
              <SignDialogPrintBlock
                v-model:print-btn-loading="printBtnLoading"
                :sign-id="sign?.id"
                :is-print-btn-disabled="isPrintBtnDisabled"
              />
            </div>
          </div>
        </VCardText>
      </VCard>
      <!-- Sidebar -->
      <SignActivityTimelineSidebar
        ref="signActivityTimelineSidebar"
        v-model:show-sidebar="showSidebar"
        v-model:sign-activity-timeline-page="signActivityTimelinePage"
        :activity-timeline-items="signActivityTimelineItems"
        :sign-activity-timeline-is-next-page-exist="signActivityTimelineIsNextPageExist"
        :loading="signActivityTimelineLoading"
        :sign-id="props.signId"
        :can-add-comment="sign.is_active_campaign"
        @onAdd="getSignActivityTimelineItems(true)"
        @loadMoreActivityItems="getSignActivityTimelineItems(false)"
      />
    </VDialog>
  </div>
</template>

<style lang="scss">
.sign-modal {
  .v-card {
    box-shadow: rgba(0, 0, 0, 0.24) 0 3px 8px !important;
  }

  .ghost-draggable {
    opacity: 0.5;
    background: rgb(var(--v-theme-primary), 0.2);
  }

  .add-pictogram-block {
    &:hover {
      background-color: rgb(var(--v-theme-on-surface), 0.1);
    }
  }
}
</style>
